import axios, { getServerErrorMessage, dispatchError } from '../utils/axios'
import * as c from '../constants/SettingsConstants'
import { DEFAULT_REQUEST_ERROR } from '../constants/MessageConstants'
import { normalizeSSDMaterials } from '../utils/normalize'
import { sortColumnByKey } from '../utils/sort'

export const setSelectedEducationPeriod = code => (dispatch, getState) => {
  const isCurrent =
    getState().settingsEducationPeriod.availableEducationPeriods[code].chronology === 'current'
  dispatch({ type: c.SET_SELECTED_EDUCATION_PERIOD, payload: code })
  dispatch({ type: c.SET_SELECTED_IS_CURRENT_EDUCATION_PERIOD, payload: isCurrent })
}

export const clearSelectedEducationPeriod = () => dispatch => {
  dispatch({ type: c.CLEAR_SELECTED_EDUCATION_PERIOD })
  dispatch({ type: c.SET_SELECTED_IS_CURRENT_EDUCATION_PERIOD, payload: false })
}

export const fetchEducationPeriods = getOnlyCurrent => async dispatch => {
  dispatch({ type: c.FETCH_EDUCATION_PERIOD_PENDING })
  const periodsToGet = `${!getOnlyCurrent ? 'include=predecessor&' : ''}include=current`
  try {
    const { data } = await axios.get(
      `${Config.API_URL}/paapro/v1/referencedata/education-periods?${periodsToGet}`
    )
    let availableEducationPeriods = {}
    let current = null
    let previous = null
    if (Array.isArray(data)) {
      availableEducationPeriods = data.reduce((obj, period) => {
        if (period.chronology === 'current') current = period.code
        else if (period.chronology === 'predecessor') previous = period.code
        return { ...obj, [period.code]: { ...period } }
      }, {})
    } else {
      availableEducationPeriods = { [data.code]: data }
      current = data.code
    }
    dispatch({
      type: c.FETCH_EDUCATION_PERIOD_FULFILLED,
      payload: {
        availableEducationPeriods,
        current,
        previous,
      },
    })
    dispatch(setSelectedEducationPeriod(current))
  } catch (err) {
    dispatch({
      type: c.FETCH_EDUCATION_PERIOD_REJECTED,
      payload: 'Error: Could not retrieve academic period data. Please try again later.',
    })
  }
}

export const fetchAllowedSSDMaterials = () => async dispatch => {
  try {
    dispatch({ type: c.FETCH_ALLOWED_SSD_MATERIALS_PENDING })
    const res = await axios.get(`${Config.API_URL}/settings/allowed-ssd-materials`)
    const data = await res.data
    dispatch({
      type: c.FETCH_ALLOWED_SSD_MATERIALS_FULFILLED,
      payload: normalizeSSDMaterials(data),
    })
  } catch (err) {
    dispatch({
      type: c.FETCH_ALLOWED_SSD_MATERIALS_REJECTED,
      payload: getServerErrorMessage(err || DEFAULT_REQUEST_ERROR),
    })
  }
}

export const fetchContactInformation = (orgId, aiCode) => async (dispatch, getState) => {
  const educationPeriodCd = getState().settingsEducationPeriod.current
  const VISITING_ADDR_TYPE_CD = '1'
  const SHIPPING_ADDR_TYPE_CD = '3'

  dispatch({ type: c.FETCH_CONTACT_INFORMATION_PENDING })
  // Fetch AI shipping address
  try {
    const {
      data: {
        hits: { hits = [] },
      },
    } = await axios.get(`${Config.CBAPI_ORGCDS.url}/aisearch?aicode=${aiCode}&aidomain=ap`, {
      withCredentials: false,
      transformRequest: (data, headers) => {
        headers.common = { 'X-Api-Key': Config.CBAPI_ORGCDS.apiKey }
        return data
      },
    })
    const searchResult = hits?.[0]._source
    const { org_full_name } = searchResult
    const defaultAddress =
      searchResult.org_addresses.find(adr => adr.addr_type_cd === VISITING_ADDR_TYPE_CD) ?? {}
    const addr =
      searchResult.org_addresses.find(adr => adr.addr_type_cd === SHIPPING_ADDR_TYPE_CD) ??
      defaultAddress
    const shippingAddress = addr
      ? {
          schoolName: org_full_name,
          state: addr.state_cd,
          city: addr.city,
          countryCode: addr.country_iso_cd,
          streetAddr1: addr.street_addr1,
          streetAddr2: addr.street_addr2,
          zip5: addr.zip5,
        }
      : null
    // Fetch coordinator info
    const { data: coordinator } = await axios.get(`${Config.API_URL}/settings/coordinator-info`, {
      params: { orgId, educationPeriodCd },
    })
    // Operation successful
    dispatch({
      type: c.FETCH_CONTACT_INFORMATION_FULFILLED,
      payload: { orgId, data: { shippingAddress, coordinator } },
    })
  } catch (err) {
    dispatch({
      type: c.FETCH_CONTACT_INFORMATION_REJECTED,
      payload: getServerErrorMessage(err || DEFAULT_REQUEST_ERROR),
    })
  }
}

const generateExamWindowRefData = data => {
  const examWindowRefData = {}
  Object.keys(data).forEach(testCd => {
    Object.keys(data[testCd]).forEach(adminWindowName => {
      const {
        adminWindow,
        displayName,
        sequence,
        requiresUnlocking,
        examFormat,
        etsTestWindowLabel,
        testAdmin,
      } = data?.[testCd]?.[adminWindowName]
      Object.assign(examWindowRefData, {
        [adminWindowName]: {
          adminWindow,
          displayName,
          sequence,
          requiresUnlocking,
          examFormat,
          etsTestWindowLabel,
          testAdmin,
        },
      })
    })
  })
  return examWindowRefData
}

const generateExamDateRefData = data => {
  const examDateRefDataArr = []
  Object.keys(data).forEach(adminWindowName => {
    examDateRefDataArr.push(data[adminWindowName])
  })
  const sortedExamDateRefData = sortColumnByKey(examDateRefDataArr, ['sequence'], ['asc'])
  const examDateTypes = new Set()
  sortedExamDateRefData.map(item => {
    examDateTypes.add(item.testAdmin)
  })
  return Array.from(examDateTypes)
}

export const fetchExamWindows = educationPeriodCd => async dispatch => {
  try {
    dispatch({ type: c.FETCH_EXAM_WINDOWS_PENDING })
    const { data } = await axios.get(
      `${Config.API_URL}/paapro/v1/exam-windows/education-periods/${educationPeriodCd}`
    )
    const examWindowDataForCourses = {}
    Object.keys(data).forEach(course => {
      data[course].forEach(visibleWindow => {
        examWindowDataForCourses[course] = {
          ...examWindowDataForCourses[course],
          [visibleWindow.adminWindow]: {
            ...visibleWindow,
            displayName: visibleWindow.etsTestWindowLabel,
          },
        }
      })
    })
    // console.log('*** exam windows', examWindowDataForCourses)
    const examWindowRefData = generateExamWindowRefData(examWindowDataForCourses)
    // console.log('*** examWindowRefData', examWindowRefData)
    const examDateRefData = generateExamDateRefData(examWindowRefData)
    dispatch({
      type: c.FETCH_EXAM_WINDOWS_FULFILLED,
      payload: { data: examWindowDataForCourses, examWindowRefData, examDateRefData },
    })
  } catch (err) {
    // console.log('fetchExamWindows Error:', err)
    dispatchError(dispatch, c.FETCH_EXAM_WINDOWS_REJECTED, err)
  }
}
